import React, { useState } from "react";
import { MdOutlineClose } from "react-icons/md";
import {
  FaCalendarAlt,
  FaUser,
  FaMapMarkerAlt,
  FaDesktop,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

function LoginHistoryModal({
  isOpen,
  closeModel,
  previewHeading,
  data,
  childrenClassName = "",
}) {
  if (!isOpen) {
    return null;
  }

  const Tabs = ({ tabs, activeTab, onTabChange }) => {
    return (
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
              <span className="ml-2 bg-gray-100 text-gray-600 py-1 px-2 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>
    );
  };

  const LoginDataDisplay = ({ data }) => {
    const [activeTab, setActiveTab] = useState("all");

    const formatDate = (timestamp) => {
      return new Date(timestamp).toLocaleString();
    };

    const getStatusColor = (status) => {
      return status === "SUCCESS" ? "text-green-600" : "text-red-600";
    };

    const getStatusIcon = (status) => {
      return status === "SUCCESS" ? (
        <FaCheckCircle className="w-5 h-5 text-green-600" />
      ) : (
        <FaTimesCircle className="w-5 h-5 text-red-600" />
      );
    };

    const getFilteredData = () => {
      switch (activeTab) {
        case "successful":
          return data.filter((login) => login.status === "SUCCESS");
        case "failed":
          return data.filter((login) => login.status === "FAILURE");
        case "recent":
          const sortedData = [...data].sort(
            (a, b) => new Date(b.loginTime) - new Date(a.loginTime)
          );
          return sortedData.slice(0, 10);
        default:
          return data;
      }
    };

    const filteredData = getFilteredData();
    const successCount = data.filter(
      (login) => login.status === "SUCCESS"
    ).length;
    const failureCount = data.filter(
      (login) => login.status === "FAILURE"
    ).length;

    const tabs = [
      { id: "all", label: "All Logins", count: data.length },
      { id: "successful", label: "Successful", count: successCount },
      { id: "failed", label: "Failed", count: failureCount },
      { id: "recent", label: "Recent", count: Math.min(10, data.length) },
    ];

    return (
      <div>
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No data found for the selected filter.
            </div>
          ) : (
            filteredData.map((login, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-3 border border-gray-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="flex items-center space-x-2">
                    <FaUser className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700">User</p>
                      <p className="text-xs text-gray-600 truncate">
                        {login.user}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaCalendarAlt className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700">
                        Login Time
                      </p>
                      <p className="text-xs text-gray-600">
                        {formatDate(login.loginTime)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(login.status)}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700">
                        Status
                      </p>
                      <p
                        className={`text-xs font-medium ${getStatusColor(
                          login.status
                        )}`}
                      >
                        {login.status}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaMapMarkerAlt className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700">
                        IP Address
                      </p>
                      <p className="text-xs text-gray-600">{login.ipAddress}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaDesktop className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700">
                        User Agent
                      </p>
                      <p className="text-xs text-gray-600">{login.userAgent}</p>
                    </div>
                  </div>
                  
                  {login.failureReason && (
                    <div className="col-span-full">
                      <div className="bg-red-50 border border-red-200 rounded-md p-2">
                        <p className="text-xs font-medium text-red-800">
                          Failure Reason
                        </p>
                        <p className="text-xs text-red-700">
                          {login.failureReason}
                        </p>
                      </div>
                    </div>
                  )}
                  {login.location && (
                    <div className="flex items-center space-x-2">
                      <FaMapMarkerAlt className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700">
                          Location
                        </p>
                        <p className="text-xs text-gray-600">
                          {typeof login.location === "object"
                            ? `${login.location.city || ""}, ${
                                login.location.country || ""
                              }`
                            : login.location}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {filteredData.length > 0 && (
          <div className="mt-4 text-xs text-gray-500 text-center">
            Showing {filteredData.length} of {data.length} login records
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-40 bg-black bg-opacity-50">
      <div className="relative bg-[#FAFAFA] border-[#D2D2D2] text-black rounded-[6px] lg:max-w-4xl w-11/12">
        <div className="flex justify-between items-start px-6 pt-4">
          <div>
            <h1 className="text-xl font-bold text-black">{previewHeading}</h1>
          </div>
          {closeModel && (
            <div
              className="w-8 h-8 cursor-pointer rounded-full bg-[#ECECEC] hover:bg-[#ECECEC]/80 flex justify-center items-center"
              onClick={closeModel}
            >
              <MdOutlineClose title="Close" />
            </div>
          )}
        </div>
        <div className={childrenClassName}>
          <LoginDataDisplay data={data} />
        </div>
      </div>
    </div>
  );
}

export default LoginHistoryModal;
